import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { DineInQrCode, TableMaps } from 'api/controllers/tableMaps';
import { EditorTable, useTableMapWizard } from '../context';
import MapEditorCanvas, { clientToSVG } from '../components/MapEditorCanvas';
import PlacedTable from '../components/PlacedTable';
import TableHistoryControls from '../components/TableHistoryControls';
import TableOverlapNotice from '../components/TableOverlapNotice';
import TableSettingsPanel from '../components/TableSettingsPanel';
import ZoomControls from '../components/ZoomControls';
import { overlappingTableIds, rotate } from '../features';
import {
    computeRoom,
    Point,
    RoomRect,
    tableFootprintMeters,
    tableFootprintPx,
} from '../mapGeometry';
import { isGeometryEdited, sizeFromCornerDrag, TableInputs } from '../tableEdit';

const MAGENTA = theme.palette.primary.main;
const TOOLTIP_BG = '#212121';
const MAX_WARNING_COLOR = theme.palette.warning.light;
const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, Roboto, Segoe UI, sans-serif';

// Rotate handle geometry: a stem + circle placed along the current rotation
// angle, reachable beyond the figure regardless of its shape.
const ROTATE_HANDLE_GAP = 26;
const ROTATE_HANDLE_STEM = 20;
const ROTATE_HANDLE_RADIUS = 6;
const ROTATE_SNAP_DEGREES = 15;
const RESIZE_HANDLE_SIZE = 7;
const CORNER_SIGNS: Point[] = [
    { x: -1, y: -1 },
    { x: 1, y: -1 },
    { x: 1, y: 1 },
    { x: -1, y: 1 },
];

function pointAtAngle(deg: number, dist: number): Point {
    const rad = (deg * Math.PI) / 180;
    return { x: dist * Math.sin(rad), y: -dist * Math.cos(rad) };
}

/** Angle (0..359, clockwise from up) of `point` as seen from the table center. */
function angleFromCenter(dx: number, dy: number, snap: boolean): number {
    const raw = (Math.atan2(dx, -dy) * 180) / Math.PI;
    const normalized = ((raw % 360) + 360) % 360;
    if (!snap) return Math.round(normalized) % 360;
    return (Math.round(normalized / ROTATE_SNAP_DEGREES) * ROTATE_SNAP_DEGREES) % 360;
}

const useStyles = makeStyles({
    body: {
        display: 'flex',
        alignItems: 'stretch',
    },
    canvasColumn: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
        paddingRight: 24,
    },
    canvasWrapper: {
        width: '100%',
        height: 560,
        borderRadius: 4,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.grey['300']}`,
        position: 'relative' as const,
    },
    canvasOverlay: {
        position: 'absolute' as const,
        left: 16,
        bottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    sidePanel: {
        width: 272,
        flexShrink: 0,
        paddingLeft: 24,
        borderLeft: `1px solid ${theme.palette.grey['300']}`,
    },
    hintText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
});

interface TableEditHandlesProps {
    applied: EditorTable;
    shown: EditorTable;
    room: RoomRect;
    svgRef: React.RefObject<SVGSVGElement>;
    onEdit: (patch: Partial<TableInputs>) => void;
}

function TableEditHandles({ applied, shown, room, svgRef, onEdit }: TableEditHandlesProps) {
    const { draft } = useTableMapWizard();
    const activeHandle = useRef<'resize' | 'rotate' | null>(null);
    const [isResizedToMax, setIsResizedToMax] = useState(false);
    const figure = tableFootprintPx(
        shown.shape,
        shown.width,
        shown.height,
        draft.tableDefaults,
        room
    );
    const isRound = shown.shape === 'round';
    const halfW = (isRound ? Math.min(figure.width, figure.height) : figure.width) / 2;
    const halfH = (isRound ? Math.min(figure.width, figure.height) : figure.height) / 2;
    const corners = CORNER_SIGNS.map((s) =>
        rotate({ x: s.x * halfW, y: s.y * halfH }, shown.rotation)
    );
    const figHalf = Math.max(halfW, halfH);
    const stemStart = pointAtAngle(shown.rotation, figHalf + ROTATE_HANDLE_GAP);
    const handlePoint = pointAtAngle(
        shown.rotation,
        figHalf + ROTATE_HANDLE_GAP + ROTATE_HANDLE_STEM
    );
    const isEdited = isGeometryEdited(applied, shown, draft.tableDefaults);

    function pointerOffset(e: React.PointerEvent): Point | null {
        if (!svgRef.current) return null;
        const p = clientToSVG(svgRef.current, e.clientX, e.clientY);
        return {
            x: p.x - (room.x + shown.x * room.width),
            y: p.y - (room.y + shown.y * room.height),
        };
    }

    function startHandle(kind: 'resize' | 'rotate', e: React.PointerEvent<SVGElement>) {
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        activeHandle.current = kind;
    }

    function handlePointerMove(e: React.PointerEvent<SVGElement>) {
        const offset = pointerOffset(e);
        if (!offset || !activeHandle.current) return;
        if (activeHandle.current === 'rotate') {
            onEdit({ rotation: String(angleFromCenter(offset.x, offset.y, !e.shiftKey)) });
            return;
        }
        const { size, isAtMax } = sizeFromCornerDrag(offset, shown, room.pxPerMeter, draft.size);
        setIsResizedToMax(isAtMax);
        onEdit({ width: String(size.width), height: String(size.height) });
    }

    function endHandle() {
        activeHandle.current = null;
        setIsResizedToMax(false);
    }

    const handleEvents = {
        onPointerMove: handlePointerMove,
        onPointerUp: endHandle,
        onPointerCancel: endHandle,
        onLostPointerCapture: endHandle,
    };

    return (
        <>
            {isEdited && <AppliedGhost table={applied} room={room} />}
            {(isEdited || isResizedToMax) && (
                <AfterTooltip
                    table={shown}
                    extent={Math.hypot(halfW, halfH)}
                    isAtMax={isResizedToMax}
                />
            )}
            <line
                x1={stemStart.x}
                y1={stemStart.y}
                x2={handlePoint.x}
                y2={handlePoint.y}
                stroke={MAGENTA}
                strokeWidth={1.5}
                pointerEvents="none"
            />
            <circle
                cx={handlePoint.x}
                cy={handlePoint.y}
                r={ROTATE_HANDLE_RADIUS}
                fill="white"
                stroke={MAGENTA}
                strokeWidth={2}
                style={{ cursor: 'grab' }}
                onPointerDown={(e) => startHandle('rotate', e)}
                {...handleEvents}
            >
                <title>{i18n.t('tableMaps.wizard.tables.rotate')}</title>
            </circle>
            {corners.map((corner, i) => (
                <rect
                    key={i}
                    x={corner.x - RESIZE_HANDLE_SIZE / 2}
                    y={corner.y - RESIZE_HANDLE_SIZE / 2}
                    width={RESIZE_HANDLE_SIZE}
                    height={RESIZE_HANDLE_SIZE}
                    fill="white"
                    stroke={MAGENTA}
                    strokeWidth={1.5}
                    style={{ cursor: i % 2 === 0 ? 'nwse-resize' : 'nesw-resize' }}
                    onPointerDown={(e) => startHandle('resize', e)}
                    {...handleEvents}
                />
            ))}
        </>
    );
}

function AppliedGhost({ table, room }: { table: EditorTable; room: RoomRect }) {
    const { draft } = useTableMapWizard();
    const metres = tableFootprintMeters(
        table.shape,
        table.width,
        table.height,
        draft.tableDefaults
    );
    const width = metres.width * room.pxPerMeter;
    const height = metres.height * room.pxPerMeter;
    const isRound = table.shape === 'round';
    const outline = {
        fill: 'none',
        stroke: theme.palette.grey['700'],
        strokeWidth: 1,
        strokeDasharray: '4 3',
    };
    return (
        <g pointerEvents="none">
            {isRound ? (
                <circle r={Math.min(width, height) / 2} {...outline} />
            ) : (
                <rect
                    x={-width / 2}
                    y={-height / 2}
                    width={width}
                    height={height}
                    transform={table.rotation ? `rotate(${table.rotation})` : undefined}
                    {...outline}
                />
            )}
            <text
                y={Math.hypot(width, height) / 2 + 10}
                textAnchor="middle"
                fontSize={9}
                fill={theme.palette.grey['700']}
                stroke="white"
                strokeWidth={3}
                paintOrder="stroke"
                fontFamily={FONT_FAMILY}
            >
                {`${i18n.t('tableMaps.wizard.tables.before').toUpperCase()} ${metres.width} × ${
                    metres.height
                } m`}
            </text>
        </g>
    );
}

interface AfterTooltipProps {
    table: EditorTable;
    extent: number;
    isAtMax: boolean;
}

function AfterTooltip({ table, extent, isAtMax }: AfterTooltipProps) {
    const { draft } = useTableMapWizard();
    const metres = tableFootprintMeters(
        table.shape,
        table.width,
        table.height,
        draft.tableDefaults
    );
    const title = `${i18n.t('tableMaps.wizard.tables.after').toUpperCase()}  ${metres.width.toFixed(
        2
    )} × ${metres.height.toFixed(2)} m`;
    const detail = i18n.t('tableMaps.wizard.tables.notAppliedYet', { rotation: table.rotation });
    const maxReached = i18n.t('tableMaps.wizard.tables.sizeTooLarge');
    const width = Math.max(title.length * 6.4, detail.length * 4.9, maxReached.length * 4.9) + 20;
    const x = extent + 16;
    return (
        <g pointerEvents="none" fontFamily={FONT_FAMILY}>
            <rect x={x} y={-19} width={width} height={isAtMax ? 52 : 38} rx={3} fill={TOOLTIP_BG} />
            <text x={x + 10} y={-5} fontSize={11} fontWeight={700} fill="white">
                {title}
            </text>
            <text x={x + 10} y={10} fontSize={9} fill={theme.palette.grey['400']}>
                {detail}
            </text>
            {isAtMax && (
                <text x={x + 10} y={24} fontSize={9} fontWeight={700} fill={MAX_WARNING_COLOR}>
                    {maxReached}
                </text>
            )}
        </g>
    );
}

interface TableDetailsStepProps {
    originalTables: EditorTable[];
}

export default function TableDetailsStep({ originalTables }: TableDetailsStepProps) {
    const classes = useStyles();
    const { draft, dispatch } = useTableMapWizard();
    const room = useMemo(() => computeRoom(draft.size), [draft.size.width, draft.size.height]);
    const [zoom, setZoom] = useState(1.0);
    const svgRef = useRef<SVGSVGElement>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [qrCodes, setQrCodes] = useState<DineInQrCode[]>([]);
    const pending = draft.pendingTable;

    useEffect(() => {
        TableMaps.getDineInQrCodes().then(setQrCodes);
    }, []);

    useEffect(() => () => dispatch({ type: 'DISCARD_PENDING_TABLE' }), [dispatch]);

    const shownTables = useMemo(
        () =>
            pending
                ? draft.tables.map((t) => (t.id === pending.id ? { ...t, ...pending.edit } : t))
                : draft.tables,
        [draft.tables, pending]
    );
    const overlappingIds = useMemo(
        () => overlappingTableIds(shownTables, draft.features, draft.size, draft.tableDefaults),
        [shownTables, draft.features, draft.size, draft.tableDefaults]
    );
    const originalActiveQrCodes = useMemo(
        () => originalTables.filter((t) => t.isActive && !!t.qrCode).map((t) => t.qrCode as string),
        [originalTables]
    );
    const selected = draft.tables.find((t) => t.id === selectedId) ?? null;

    useEffect(() => {
        if (selectedId !== null && !selected) setSelectedId(null);
    }, [selectedId, selected]);

    function select(id: string | null) {
        if (!pending) setSelectedId(id);
    }

    function handleMove(id: string, x: number, y: number) {
        dispatch({ type: 'UPDATE_TABLE_POSITION', payload: { id, x, y } });
        select(id);
    }

    function handleEdit(patch: Partial<TableInputs>) {
        if (selected) dispatch({ type: 'EDIT_PENDING_TABLE', payload: { id: selected.id, patch } });
    }

    function handleDelete() {
        if (!selected) return;
        dispatch({ type: 'REMOVE_TABLE', payload: { id: selected.id } });
        setSelectedId(null);
    }

    return (
        <div className={classes.body}>
            <div className={classes.canvasColumn}>
                <div className={classes.canvasWrapper}>
                    <MapEditorCanvas
                        gridEnabled={draft.gridEnabled}
                        zoom={zoom}
                        size={draft.size}
                        area={draft.area}
                        openings={draft.entrances}
                        features={draft.features}
                        isFeaturesDimmed
                        svgRef={svgRef}
                        showMeasureGuides
                    >
                        <rect
                            x={room.x}
                            y={room.y}
                            width={room.width}
                            height={room.height}
                            fill="transparent"
                            onPointerDown={() => select(null)}
                        />
                        {shownTables.map((table, i) => (
                            <PlacedTable
                                key={table.id}
                                table={table}
                                room={room}
                                svgRef={svgRef}
                                isOverlapping={overlappingIds.has(table.id)}
                                isLocked={draft.tables[i].isPositionLocked}
                                onSelect={select}
                                onMove={handleMove}
                            >
                                {selected && table.id === selected.id && (
                                    <TableEditHandles
                                        applied={selected}
                                        shown={table}
                                        room={room}
                                        svgRef={svgRef}
                                        onEdit={handleEdit}
                                    />
                                )}
                            </PlacedTable>
                        ))}
                    </MapEditorCanvas>
                    <div className={classes.canvasOverlay}>
                        <ZoomControls
                            zoom={zoom}
                            onZoomChange={setZoom}
                            pxPerMeter={room.pxPerMeter}
                        />
                        <TableHistoryControls />
                    </div>
                </div>
                <TableOverlapNotice tables={shownTables} overlappingIds={overlappingIds} />
            </div>

            <div className={classes.sidePanel}>
                {selected ? (
                    <TableSettingsPanel
                        table={selected}
                        qrCodes={qrCodes}
                        originalActiveQrCodes={originalActiveQrCodes}
                        onDelete={handleDelete}
                    />
                ) : (
                    <Typography className={classes.hintText}>
                        {i18n.t('tableMaps.wizard.selectTableHint')}
                    </Typography>
                )}
            </div>
        </div>
    );
}
